import { Link } from "react-router-dom";
import homeImage from "../assets/home-zs.svg";
import TechHero from "../components/TechHero";
import TeamSection from "../components/TeamSection";
import useScrollTrigger from "../hooks/useScrollTrigger";
import useCounterAnimation from "../hooks/useCounterAnimation";
import useSEO from "../hooks/useSEO";
import { blogPosts } from "../data/blogPosts";

export default function Home() {
  const [statsRef, statsVisible] = useScrollTrigger(0.3);
  const _projectsCount = useCounterAnimation(150, 2000, statsVisible);
  const _clientsCount = useCounterAnimation(50, 2000, statsVisible);
  const techCount = useCounterAnimation(15, 2000, statsVisible);

  // SEO optimization
  useSEO(
    "Premium Web & App Development | ZS NEXATECH",
    "Elite software development company delivering high-performance websites, web apps, and platforms. Fast delivery, enterprise-grade quality, scalable architecture.",
    "web development, app development, software company, React, Node.js, full stack",
    "https://www.zsnexatech.com"
  );

  return (
    <div className="space-y-14">
      <TechHero
        kicker="ELITE PRODUCT ENGINEERING"
        title="Launch production-ready platforms in 2–6 weeks"
        titleAs="h1"
        subtitle="Secure • Scalable • Conversion-focused. We build high-performance products that look premium and perform under real traffic."
        ctaText="Book Free Consultation"
        ctaLink="/book-consultation"
      />
      <section className="animate-fade-in-up">
        <div className="w-full animate-image-levitate">
          <img
            src={homeImage}
            alt="ZS NEXATECH - From Ideas To Impact - Software Development Company"
            className="w-full rounded-3xl border border-white/50 shadow-lg hover:shadow-2xl transition-all"
            loading="lazy"
          />
        </div>
      </section>

      <section className="student-spotlight">
        <div className="student-spotlight__glow" aria-hidden="true" />
        <div className="student-spotlight__content">
          <p className="student-spotlight__badge">🎓 Student launchpad</p>
          <h2 className="student-spotlight__title">Final‑year projects built to stand out</h2>
          <p className="student-spotlight__desc">
            College‑ready websites, web apps, and software projects with documentation, PPT, ER diagrams, and demo guidance.
          </p>
        </div>
        <div className="student-spotlight__actions">
          <a href="/student-projects" className="btn-gold">Explore student projects</a>
          <a className="link-gold" href="/contact">Get guidance</a>
        </div>
      </section>

      <section ref={statsRef} className="grid gap-6 md:grid-cols-4">
        {[
          { label: "Launch Speed", value: "2-6", suffix: " wks", icon: "🚀" },
          { label: "Core Technologies", value: techCount, suffix: "+", icon: "⚡" },
          { label: "Code Quality", value: "Enterprise", suffix: "", icon: "✨" },
          { label: "Support Response", value: "24", suffix: " hrs", icon: "⭐" },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-5 text-center hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 group ${statsVisible ? 'fade-in-scale' : 'opacity-0'}`}
          >
            <div className="text-4xl mb-2 transform group-hover:scale-110 transition-transform duration-300">{stat.icon}</div>
            <div className="text-3xl font-black text-red-300 transform group-hover:scale-110 transition-transform duration-300 counter-number">
              {typeof stat.value === 'number' ? stat.value : stat.value}{stat.suffix}
            </div>
            <div className="mt-2 text-sm font-semibold text-white">{stat.label}</div>
          </div>
        ))}
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">🤝 Built for ambitious, fast-moving teams</h2>
          <p className="mt-2 text-gray-100">
            We help startups and enterprises ship faster with clearer scope, cleaner UX, and stronger reliability.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {[
            "FinTech",
            "HealthTech",
            "EdTech",
            "SaaS",
            "E-commerce",
            "Logistics",
            "Manufacturing",
            "Real Estate",
            "Travel & Transport",
          ].map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-white/30 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white/90 backdrop-blur-md"
            >
              {chip}
            </span>
          ))}
        </div>
      </section>

      <section className="premium-panel glass-panel animated-border">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <span className="section-kicker holo-gradient text-slate-900">Delivery OS</span>
            <h2 className="mt-4 text-3xl font-bold text-white">A delivery system engineered for speed + trust</h2>
            <p className="mt-3 text-gray-100">
              We run a focused build process with weekly demos, clear scope boundaries, and performance budgets so your team always knows what’s shipping and why.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {[
                "Weekly demos",
                "Performance budgets",
                "QA gates",
                "Security checks",
                "Launch playbook",
              ].map((item) => (
                <span key={item} className="icon-pill">✅ {item}</span>
              ))}
            </div>
          </div>
          <div className="grid gap-4">
            {[
              {
                title: "Design sprint",
                desc: "Wireframes, flows, and UI kit in days, not weeks.",
              },
              {
                title: "Engineering sprint",
                desc: "Clean architecture, staged rollouts, and stable releases.",
              },
              {
                title: "Launch sprint",
                desc: "Performance checks, monitoring, and go‑live support.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-5"
              >
                <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                <p className="mt-2 text-sm text-gray-100">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-white">🚀 What we deliver</h2>
          <p className="mt-2 text-gray-100">
            Premium UI, scalable architecture, and security-first delivery—engineered for real business impact.
          </p>
        </div>
        <div className="card-grid grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              title: "Websites & Landing Pages",
              desc: "Lightning-fast, mobile-first websites optimized for speed, SEO, and conversions—designed to build trust instantly.",
              icon: "🌐",
            },
            {
              title: "Web Applications",
              desc: "Full-stack portals with real-time dashboards, role-based access, and clean, scalable integrations.",
              icon: "💻",
            },
            {
              title: "Banking Software & Apps",
              desc: "Secure banking websites, customer portals, mobile apps, and compliance-ready workflows.",
              icon: "🏦",
            },
            {
              title: "Mobile Apps",
              desc: "Native-quality cross-platform apps for iOS & Android—from MVP to production-ready scale.",
              icon: "📱",
            },
            {
              title: "CRM & Business Platforms",
              desc: "Smart lead management with automation, analytics, and revenue visibility that sales teams love.",
              icon: "📊",
            },
            {
              title: "HRMS Software",
              desc: "Complete HR automation: attendance, payroll, leave management, and compliance-ready reporting.",
              icon: "👥",
            },
            {
              title: "Travel & Transportation",
              desc: "Complete booking and fleet management systems for travel agencies, cab operators, bus services, and car rentals with real-time tracking.",
              icon: "🚕",
            },
            {
              title: "UI/UX Design",
              desc: "Research-led UX and design systems that reduce friction and increase engagement.",
              icon: "🎨",
            },
            {
              title: "Maintenance & Support",
              desc: "Proactive monitoring, zero-downtime updates, and fast response SLAs for peace of mind.",
              icon: "🔧",
            },
          ].map((service) => (
            <div
              key={service.title}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 shadow-sm hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden"
            >
              <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500"></div>
              <div className="relative z-10">
                <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">{service.icon}</div>
                <h3 className="text-lg font-semibold text-white">{service.title}</h3>
                <p className="mt-2 text-sm text-gray-100">{service.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-3">
        {[
          {
            title: "Launch-ready in weeks",
            desc: "Rapid delivery without shortcuts. We ship MVPs with production-grade foundations from day one.",
            stat: "2–6 Weeks",
            icon: "⏱️",
          },
          {
            title: "Conversion-focused UX",
            desc: "Designs engineered for clarity and action—improving trust, engagement, and lead quality.",
            stat: "UX‑Led",
            icon: "🎯",
          },
          {
            title: "Security-first build",
            desc: "Secure APIs, audit-friendly logging, and best-practice controls built into every release.",
            stat: "Secure‑by‑Design",
            icon: "🔒",
          },
        ].map((item) => (
          <div
            key={item.title}
            className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-red-600/0 group-hover:from-amber-500/10 group-hover:to-red-600/10 transition-all duration-500"></div>
            <div className="relative z-10">
              <div className="text-4xl mb-2 transform group-hover:scale-125 transition-transform duration-300">{item.icon}</div>
              <div className="text-xs font-bold text-red-200 tracking-widest">{item.stat}</div>
              <h3 className="mt-2 text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{item.desc}</p>
            </div>
          </div>
        ))}
      </section>

      <section id="industries" className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-white">🏛️ Industries we transform</h2>
          <p className="mt-2 text-gray-100">
            Industry-aware solutions with the right workflows, integrations, and compliance checkpoints.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { name: "Educational Institutions", icon: "🎓" },
            { name: "Coaching Centers", icon: "📚" },
            { name: "Restaurants & Cafes", icon: "🍽️" },
            { name: "Salons & Wellness", icon: "💆" },
            { name: "Clinics & Hospitals", icon: "🏥" },
            { name: "E-commerce Stores", icon: "🛒" },
            { name: "Real Estate Agencies", icon: "🏢" },
            { name: "Automobile Dealerships", icon: "🚗" },
            { name: "Travel & Transportation", icon: "🚕" },
            { name: "Professional Services", icon: "💼" },
            { name: "Startups & SMEs", icon: "🚀" },
          ].map((item) => (
            <div
              key={item.name}
              className="card rounded-xl border border-white/30 bg-white/10 backdrop-blur-md px-4 py-3 text-sm font-medium text-white hover:bg-red-500/20 hover:border-red-400/50 hover:scale-105 hover:-translate-y-1 transition-all duration-300 group flex items-center gap-2"
            >
              <span className="text-lg transform group-hover:scale-125 transition-transform duration-300">{item.icon}</span>
              <span>{item.name}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="tech" className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-white">💻 Battle-tested technology stack</h2>
          <p className="mt-2 text-gray-100">
            Production-proven technologies optimized for performance, reliability, and long-term scale.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500"></div>
            <div className="relative z-10">
              <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">⚖️</div>
              <h3 className="text-lg font-semibold text-white">💎 Frontend Excellence</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-100">
                <li className="flex items-center"><span className="text-amber-400 mr-2">✓</span> React 18 + TypeScript</li>
                <li className="flex items-center"><span className="text-amber-400 mr-2">✓</span> Angular 17 (Enterprise)</li>
                <li className="flex items-center"><span className="text-amber-400 mr-2">✓</span> Tailwind CSS + Animations</li>
                <li className="flex items-center"><span className="text-amber-400 mr-2">✓</span> Lighthouse Score 95+</li>
              </ul>
            </div>
          </div>
          <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-green-500/0 to-emerald-600/0 group-hover:from-green-500/10 group-hover:to-emerald-600/10 transition-all duration-500"></div>
            <div className="relative z-10">
              <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">⚙️</div>
              <h3 className="text-lg font-semibold text-white">⚙️ Backend Power</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-100">
                <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Java Spring Boot 3.x</li>
                <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> RESTful & GraphQL APIs</li>
                <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> JWT + OAuth2 Security</li>
                <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Microservices Ready</li>
              </ul>
            </div>
          </div>
          <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-purple-500/0 to-violet-600/0 group-hover:from-purple-500/10 group-hover:to-violet-600/10 transition-all duration-500"></div>
            <div className="relative z-10">
              <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">🗄️</div>
              <h3 className="text-lg font-semibold text-white">🗄️ Database Mastery</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-100">
                <li className="flex items-center"><span className="text-purple-400 mr-2">✓</span> MySQL 8.x / SQL Server</li>
                <li className="flex items-center"><span className="text-purple-400 mr-2">✓</span> PostgreSQL + Indexing</li>
                <li className="flex items-center"><span className="text-purple-400 mr-2">✓</span> Redis Caching Layer</li>
                <li className="flex items-center"><span className="text-purple-400 mr-2">✓</span> Query Optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">🌍 Serving Global Clients Worldwide</h2>
          <p className="mt-2 text-gray-100">Work with teams across continents, in your timezone.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: "🇮🇳",
              region: "India",
              desc: "Base location: Ahmedabad, Gujarat",
              timezone: "IST (UTC+5:30)",
            },
            {
              icon: "🇺🇸",
              region: "United States",
              desc: "Serving startups & enterprises across US",
              timezone: "EST/PST (UTC-5 to -8)",
            },
            {
              icon: "🇬🇧",
              region: "Europe",
              desc: "Delivering solutions for UK & EU markets",
              timezone: "GMT/CET (UTC+0 to +2)",
            },
            {
              icon: "🌏",
              region: "Asia-Pacific",
              desc: "Expanding across Southeast Asia & Australia",
              timezone: "SGT/AEST (UTC+8 to +10)",
            },
          ].map((location) => (
            <div
              key={location.region}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 group"
            >
              <div className="text-5xl mb-3 transform group-hover:scale-125 transition-transform duration-300">{location.icon}</div>
              <h3 className="font-bold text-white text-lg">{location.region}</h3>
              <p className="mt-2 text-sm text-gray-100">{location.desc}</p>
              <p className="mt-3 text-xs text-amber-300 font-semibold">{location.timezone}</p>
            </div>
          ))}
        </div>

        {/* Global Reach Benefits */}
        <div className="grid gap-6 md:grid-cols-2">
          {[
            {
              point: "24/7 Support Coverage",
              detail: "Work with founders across multiple timezones. Always someone available to help.",
              icon: "⏰",
            },
            {
              point: "Global Compliance Standards",
              detail: "GDPR, HIPAA, CCPA, and local data protection laws built into every project.",
              icon: "🔐",
            },
            {
              point: "Multilingual Team",
              detail: "Communicate in English, Hindi, or coordinate with local teams in your region.",
              icon: "🗣️",
            },
            {
              point: "International Payment Methods",
              detail: "Work with clients worldwide. Invoicing in USD, EUR, GBP, INR, and more.",
              icon: "💳",
            },
          ].map((benefit) => (
            <div
              key={benefit.point}
              className="glass-panel animated-border p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="text-4xl shrink-0">{benefit.icon}</div>
                <div>
                  <h3 className="font-bold text-white text-lg">{benefit.point}</h3>
                  <p className="mt-2 text-sm text-gray-100">{benefit.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">⭐ Outcome snapshots</h2>
          <p className="mt-2 text-gray-100">Representative outcomes from delivery-focused engagements.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              name: "SaaS onboarding",
              quote: "Streamlined flows and guided setup reduced drop‑offs and improved time‑to‑value.",
              metric: "Faster onboarding",
            },
            {
              name: "Healthcare portal",
              quote: "Security-first UX with clear audit trails helped teams move faster with confidence.",
              metric: "Compliance‑ready",
            },
            {
              name: "E‑commerce platform",
              quote: "Performance budgets and UX refinements led to stronger conversion quality.",
              metric: "Higher conversions",
            },
          ].map((story) => (
            <div
              key={story.name}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300"
            >
              <div className="text-xs font-semibold text-red-200 tracking-widest">{story.metric}</div>
              <h3 className="mt-2 text-lg font-semibold text-white">{story.name}</h3>
              <p className="mt-2 text-sm text-gray-100">“{story.quote}”</p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">🎓 Final‑year project development for students</h2>
          <p className="mt-2 text-gray-100">
            We build college projects in your required tech stack with documentation, demo setup, and deployment guidance.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Custom tech stack",
              desc: "Choose React, Node, Spring Boot, Python, PHP, MySQL, MongoDB, or your college‑approved stack.",
              meta: "Stack‑matched",
            },
            {
              title: "Complete project kit",
              desc: "Includes source code, report templates, PPT, ER diagrams, and step‑by‑step setup instructions.",
              meta: "Docs ready",
            },
            {
              title: "Demo & submission support",
              desc: "We help with deployment, live demo preparation, and last‑minute fixes before submission.",
              meta: "Demo support",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300"
            >
              <div className="text-xs font-semibold text-red-200 tracking-widest">{item.meta}</div>
              <h3 className="mt-2 text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <a href="/contact" className="btn-gold">Request student project</a>
          <a className="link-gold" href="/contact">Get tech stack guidance</a>
        </div>
      </section>

      <section id="process" className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-white">⚙️ Our proven 4-step process</h2>
          <p className="mt-2 text-gray-100">Transparent workflow with weekly demos and milestone-based delivery.</p>
        </div>
        <ol className="grid gap-4 md:grid-cols-2">
          {[
            ["1️⃣ Discovery & Planning", "Deep-dive workshops, user personas, technical roadmap, and project timeline with clear milestones."],
            ["2️⃣ Design & Prototype", "Clickable prototypes, design system creation, user flow mapping, and stakeholder review sessions."],
            ["3️⃣ Development & Testing", "Agile sprints, weekly demos, automated testing, code reviews, and security audits."],
            ["4️⃣ Launch & Scale", "Deployment, performance monitoring, user training, documentation, and 90-day support."],
          ].map(([title, desc]) => (
            <li key={title} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-5 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
              <h3 className="text-base font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm text-gray-100">{desc}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] items-center">
        <div className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-7 hover:shadow-2xl hover:bg-white/20 transition-all duration-300 glass-panel animated-border">
          <span className="section-kicker">Featured build</span>
          <h2 className="mt-4 text-3xl font-bold text-white">Scale-ready platform in 6 weeks</h2>
          <p className="mt-3 text-gray-100">
            A high‑growth client needed a secure, conversion‑first web platform. We delivered a fast, SEO‑optimized experience with CRM automation and analytics.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              "95+ Lighthouse score",
              "Automated lead routing",
              "Role‑based access",
              "Analytics dashboards",
            ].map((item) => (
              <div key={item} className="icon-pill holo-gradient text-slate-900">⭐ {item}</div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="/portfolio" className="btn-gold">See more work</a>
            <a href="/contact" className="link-gold">Build something similar</a>
          </div>
        </div>
        <div className="premium-panel glass-panel animated-border">
          <h3 className="text-2xl font-bold text-white">Execution timeline</h3>
          <div className="mt-5 timeline">
            {[
              ["Week 1", "Discovery, requirements, UX mapping"],
              ["Week 2–3", "Design system + prototype reviews"],
              ["Week 4–5", "Development sprints + QA"],
              ["Week 6", "Launch, monitoring, support"],
            ].map(([title, desc]) => (
              <div key={title} className="timeline-step">
                <span className="timeline-dot" aria-hidden="true" />
                <h4 className="text-white font-semibold">{title}</h4>
                <p className="text-sm text-gray-100">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">✨ Why ZS NEXATECH</h2>
          <p className="mt-2 text-gray-100">
            Proof-focused delivery designed for startups and growth-stage businesses.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: "⚡",
              title: "Fast delivery",
              desc: "Launch products in weeks with clear milestones and weekly demos.",
            },
            {
              icon: "🔒",
              title: "Secure architecture",
              desc: "Enterprise-level security, safe APIs, and audit-friendly engineering standards.",
            },
            {
              icon: "📈",
              title: "Scalable systems",
              desc: "Built for growth with modular architecture and cloud-ready infrastructure.",
            },
            {
              icon: "🎨",
              title: "UX-first design",
              desc: "Conversion-optimized journeys that improve trust, usability, and lead quality.",
            },
          ].map((benefit) => (
            <div
              key={benefit.title}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden"
            >
              <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-red-600/0 group-hover:from-amber-500/10 group-hover:to-red-600/10 transition-all duration-500"></div>
              <div className="relative z-10">
                <div className="text-5xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">{benefit.icon}</div>
                <h3 className="font-bold text-white">{benefit.title}</h3>
                <p className="mt-2 text-sm text-gray-100">{benefit.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="engagement" className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">💼 Engagement models</h2>
          <p className="mt-2 text-gray-100">Flexible delivery options based on stage, scope, and business goals.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Startup MVP",
              timeline: "2–4 Weeks",
              desc: "Ideal for validation: product blueprint, core features, and launch-ready deployment.",
            },
            {
              title: "Product development",
              timeline: "4–8 Weeks",
              desc: "End-to-end design and engineering for market-ready products with growth foundations.",
            },
            {
              title: "Enterprise platform",
              timeline: "8+ Weeks",
              desc: "Complex, secure, and scalable systems with integrations, automation, and governance.",
            },
          ].map((plan) => (
            <div key={plan.title} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
              <p className="text-xs tracking-widest text-red-200 font-semibold">ENGAGEMENT MODEL</p>
              <h3 className="mt-2 text-xl font-bold text-white">{plan.title}</h3>
              <p className="mt-2 text-2xl font-black text-amber-300">{plan.timeline}</p>
              <p className="mt-3 text-sm text-gray-100">{plan.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="insights" className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">📝 Insights for founders</h2>
          <p className="mt-2 text-gray-100">Practical guidance on architecture, scale, and product execution.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {blogPosts.map((post) => (
            <article key={post.slug} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
              <p className="text-xs font-semibold tracking-widest text-red-200">{post.category}</p>
              <h3 className="mt-2 text-lg font-semibold text-white">{post.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{post.excerpt}</p>
              <Link to={`/blog/${post.slug}`} className="link-gold mt-3 inline-block">Read full article</Link>
            </article>
          ))}
        </div>
        <div className="text-center">
          <Link to="/blog" className="btn-gold">View all insights</Link>
        </div>
      </section>

      <section id="founder" className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">👨‍💻 Founder Spotlight</h2>
          <p className="mt-2 text-gray-100">
            Founder-led delivery with full-stack ownership and a product-first mindset.
          </p>
        </div>
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="text-6xl">👨‍💻</div>
              <div>
                <h3 className="text-2xl font-bold text-white">Tarannum Mushtaq Ahmed</h3>
                <p className="text-amber-400 text-sm font-semibold mt-1">Founder & Lead Full Stack Developer</p>
                <p className="mt-3 text-sm text-gray-100">
                  Full-stack developer and founder of ZS NEXATECH, dedicated to transforming ideas into impactful digital solutions.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/contact" className="btn-gold">Connect with founder</a>
              <a href="/founder" className="link-gold">View full profile</a>
            </div>
          </div>
          <div className="premium-panel glass-panel animated-border">
            <h3 className="text-2xl font-bold text-white">Founder focus areas</h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-100">
              <li className="icon-pill">✅ Product strategy & discovery workshops</li>
              <li className="icon-pill">✅ Architecture, scalability, and performance</li>
              <li className="icon-pill">✅ Security-first delivery & QA leadership</li>
              <li className="icon-pill">✅ Direct communication & weekly demos</li>
            </ul>
            <div className="mt-5 text-xs text-red-200 tracking-widest">Founder-led delivery</div>
          </div>
        </div>
      </section>

      {/* Lead Magnet / Free Resources */}
      <section className="card rounded-3xl border border-amber-400/40 bg-linear-to-r from-amber-500/15 to-orange-500/15 backdrop-blur-lg p-8 hover:shadow-2xl hover:border-amber-300/60 transition-all duration-300">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl font-bold text-white mb-3">📖 Free SaaS Architecture Guide</h2>
            <p className="text-gray-100 mb-4">
              Learn how to build scalable SaaS platforms without overengineering. Our free guide covers:
            </p>
            <ul className="space-y-2 text-sm text-gray-100 mb-6">
              <li>✓ Architecture patterns for fast scaling</li>
              <li>✓ Database design best practices</li>
              <li>✓ Security implementation checklist</li>
              <li>✓ Cost optimization strategies</li>
              <li>✓ Performance tuning guide</li>
            </ul>
            <a href="/contact" className="btn-gold inline-block">Get Free Guide (PDF)</a>
          </div>
          <div className="text-center">
            <div className="text-6xl mb-3">📚</div>
            <p className="text-white font-semibold mb-4">15-Page Practical Guide</p>
            <p className="text-xs text-gray-300">No credit card required • Instant download</p>
          </div>
        </div>
      </section>

      {/* Achievements / Milestones */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">🎯 Our Startup Journey</h2>
          <p className="mt-2 text-gray-100">Building fast, shipping quality, growing strong.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-4">
          {[
            { number: "50+", label: "Projects Launched", icon: "🚀" },
            { number: "150+", label: "Clients Served", icon: "👥" },
            { number: "98%", label: "Client Satisfaction", icon: "⭐" },
            { number: "2026", label: "Year Founded", icon: "🎂" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 text-center hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 group"
            >
              <div className="text-4xl mb-3 transform group-hover:scale-125 transition-transform duration-300">{stat.icon}</div>
              <div className="text-3xl font-black text-amber-300 group-hover:text-amber-200 transition-colors">{stat.number}</div>
              <p className="mt-3 text-sm font-semibold text-white">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us - Differentiators */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">💡 Why Teams Choose ZS NEXATECH</h2>
          <p className="mt-2 text-gray-100">We're not just developers. We're your product partners.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: "👨‍💼",
              title: "Founder-Led",
              desc: "Direct access to founders who care about your success, not just billable hours.",
            },
            {
              icon: "⚡",
              title: "Speed Without Shortcuts",
              desc: "Ship in 2–6 weeks with production-grade architecture that scales from day one.",
            },
            {
              icon: "🎯",
              title: "Outcome Focused",
              desc: "We measure success by your business metrics—conversions, users, revenue.",
            },
            {
              icon: "🔒",
              title: "Security First",
              desc: "Enterprise security standards from MVP stage, not an afterthought.",
            },
            {
              icon: "📈",
              title: "Scalability Built In",
              desc: "Start lean, scale fast. Architecture ready for 10x growth.",
            },
            {
              icon: "💰",
              title: "Cost Transparent",
              desc: "No hidden fees. Clear scope, clear timeline, clear cost breakdown.",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 group"
            >
              <div className="text-4xl mb-3 transform group-hover:scale-125 transition-transform duration-300">{item.icon}</div>
              <h3 className="font-bold text-white text-lg">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tech Stack Comparison */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">⚙️ Our Technology Philosophy</h2>
          <p className="mt-2 text-gray-100">We choose tools that help you ship fast and scale smart.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              category: "Frontend Performance",
              why: "React 18 + TypeScript ensure type safety and fast renders.",
              benefit: "95+ Lighthouse scores, instant page loads",
            },
            {
              category: "Backend Scalability",
              why: "Java Spring Boot and Node.js handle millions of requests.",
              benefit: "99.9% uptime, seamless scaling under load",
            },
            {
              category: "Database Optimization",
              why: "PostgreSQL + indexing + Redis caching.",
              benefit: "Sub-50ms query times, optimized costs",
            },
            {
              category: "DevOps & Security",
              why: "Vercel + GitHub Actions + SSL encryption.",
              benefit: "Zero-downtime deployments, automated security scans",
            },
          ].map((item) => (
            <div key={item.category} className="glass-panel animated-border p-6">
              <h3 className="font-bold text-white text-lg mb-2">{item.category}</h3>
              <p className="text-sm text-gray-100 mb-3">
                <strong>Why:</strong> {item.why}
              </p>
              <p className="text-sm text-amber-300">
                <strong>You get:</strong> {item.benefit}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Team Section */}
      <TeamSection />

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">❓ Frequently asked questions</h2>
          <p className="mt-2 text-gray-100">Quick answers to help you move faster.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              q: "How fast can you launch?",
              a: "Most MVPs ship in 2–6 weeks depending on scope. We provide a detailed timeline after discovery.",
            },
            {
              q: "Do you handle design and development?",
              a: "Yes. We deliver strategy, UX/UI, development, QA, and launch support as one cohesive team.",
            },
            {
              q: "Can you work with existing systems?",
              a: "Absolutely. We integrate with CRMs, ERPs, analytics tools, and custom APIs.",
            },
            {
              q: "What happens after launch?",
              a: "We provide monitoring, updates, and optimization with optional ongoing support plans.",
            },
          ].map((item) => (
            <details key={item.q} className="faq-item glass-panel animated-border">
              <summary>{item.q}</summary>
              <p className="mt-2 text-sm text-gray-100">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">🏆 Built with enterprise-grade standards</h2>
          <p className="mt-2 text-gray-100">
            Certified processes and industry-leading tools ensure quality, security, and performance.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-4">
          {[
            { icon: "🔒", title: "SSL Secured", desc: "256-bit encryption" },
            { icon: "⚡", title: "Performance", desc: "90+ Lighthouse scores" },
            { icon: "♿", title: "Accessibility", desc: "WCAG 2.1 compliant" },
            { icon: "📱", title: "Responsive", desc: "Mobile-first design" },
            { icon: "🛡️", title: "Security", desc: "OWASP guidelines" },
            { icon: "🚀", title: "Optimized", desc: "CDN & caching" },
            { icon: "📊", title: "Analytics", desc: "GA4 integrated" },
            { icon: "🔄", title: "CI/CD", desc: "Automated deploys" },
          ].map((badge) => (
            <div
              key={badge.title}
              className="card rounded-xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 text-center hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 group"
            >
              <div className="text-3xl mb-2 transform group-hover:scale-125 transition-transform duration-300">{badge.icon}</div>
              <h3 className="text-sm font-bold text-white">{badge.title}</h3>
              <p className="text-xs text-gray-100 mt-1">{badge.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <h2 className="text-3xl font-bold text-white">🚀 Ready to build with a premium team?</h2>
        <p className="mt-2 max-w-2xl text-gray-100">
          Get a focused plan, clear timeline, and detailed roadmap within 24 hours—no commitments required.
        </p>
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-100">
          <span>⚡ Free consultation</span>
          <span>📋 Detailed roadmap</span>
          <span>⏱️ 24‑hour response</span>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href="/contact" className="btn-gold consultation-booking">Get free consultation</a>
          <a className="link-gold consultation-booking" href="/book-consultation">
            Schedule a call
          </a>
        </div>
      </section>
    </div>
  );
}
